import React, { useEffect, useState } from "react";
import { Button, Switch } from "antd";
import { Footer, LoadingIcon, Sidebar, TopDropdown } from "../../../components";
import Dropdowns from "./Dropdowns";
import dayjs from "dayjs";
import { UI_DATE_FORMAT } from "../../../consts";
import { LineChartOutlined } from "@ant-design/icons";
import Graph from "./Graph";
import { COLORS } from "../../../consts";
import {
  getTotalYield,
  weekToNumber,
  isWeekInRange,
  getSiteForecastConfig,
  filterEmptyDates,
  buildFilteredDateHeaders,
} from "./utils";
import AnalysisTable from "./AnalysisTable";
import { fetchVarietyData } from "../../../utils/api";

const Reporting = ({
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  siteData,
  setSiteData,
  currentSite,
  setCurrentSite,
  siteLoading,
  setSiteLoading,
  currentCrop,
  setCurrentCrop,
  cropLoading,
  setCropLoading,
  cropData,
  setCropData,
  searchParams,
  setSearchParams,
  settingDefaults,
  setSettingDefaults,
}) => {
  // Basic states
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Multi-site states
  const [currentSites, setCurrentSites] = useState([currentSite]);
  const [greenhouseData, setGreenhouseData] = useState([]);
  const [currentGreenhouse, setCurrentGreenhouse] = useState([]);
  const [varietyData, setVarietyData] = useState([]);
  const [currentVariety, setCurrentVariety] = useState([]);

  // Date range states
  const [siteMetadata, setSiteMetadata] = useState({});
  const [currentDate, setCurrentDate] = useState(null);
  const [fromWeek, setFromWeek] = useState(null);
  const [toWeek, setToWeek] = useState(null);
  const [dateHeaders, setDateHeaders] = useState([]);
  const [forcastDate, setForcastDate] = useState(null);

  // Metric states
  const [currentMetric, setCurrentMetric] = useState(null);
  const [metricType, setMetricType] = useState(null);
  const [metricData, setMetricData] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  // Messages
  const [errorMessage, setErrorMessage] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);

  // Table/analysis data
  const [analysisTableData, setAnalysisTableData] = useState({});
  const [displayAnalysisData, setDisplayAnalysisData] = useState({});
  const [analysisDataSource, setAnalysisDataSource] = useState([]);

  // Loading maps and caches
  const [varietyLoadingMap, setVarietyLoadingMap] = useState({});
  const [fetchDataCache, setFetchDataCache] = useState({});

  // Table expansions & columns
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [showMetricColumn, setShowMetricColumn] = useState(true);

  // Graph states
  const [graphVisible, setGraphVisible] = useState(false);
  const [chartLines, setChartLines] = useState([]);
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphDateHeaders, setGraphDateHeaders] = useState([]);

  // Summaries/Detailed
  const [reportView, setReportView] = useState("Summary (All)");

  // For merging all sites
  const [mergeAllSites, setMergeAllSites] = useState(false);
  const showMergeSwitch =
    ["Summary (GH)", "Summary (Variety)", "Detailed"].includes(reportView) &&
    currentSites.length > 1;

  const viewType = [
    { label: "Summary (All)", value: "Summary (All)" },
    { label: "Summary (Site)", value: "Summary (Site)" },
    { label: "Summary (GH)", value: "Summary (GH)" },
    { label: "Summary (Variety)", value: "Summary (Variety)" },
    { label: "Detailed", value: "Detailed" },
  ];

  // Table-level loading
  const [tableLoading, setTableLoading] = useState(false);

  // -------------------------------
  // Effects: Handle fromWeek & toWeek
  // -------------------------------
  useEffect(() => {
    if (!currentSites?.length) {
      setAnalysisTableData({});
      setDisplayAnalysisData({});
      setAnalysisDataSource([]);
      setErrorMessage("No sites selected.");
      return;
    }

    // Set mergeAllSites to false when only one site
    if (currentSites.length <= 1) {
      setMergeAllSites(false);
    } else {
      setMergeAllSites(true);
    }

    let globalFromYear = 9999,
      globalFromWeek = 9999;
    let globalToYear = 0,
      globalToWeek = 0;
    let anyValid = false;

    currentSites.forEach((siteObj) => {
      const meta = siteObj?.metadata?.[currentCrop?.name];
      if (!meta) return;
      const { FROM_YEAR, FROM_WEEK, TO_YEAR, TO_WEEK } = meta;
      if (FROM_YEAR && FROM_WEEK && TO_YEAR && TO_WEEK) {
        anyValid = true;
        if (
          FROM_YEAR < globalFromYear ||
          (FROM_YEAR === globalFromYear && FROM_WEEK < globalFromWeek)
        ) {
          globalFromYear = FROM_YEAR;
          globalFromWeek = FROM_WEEK;
        }
        if (
          TO_YEAR > globalToYear ||
          (TO_YEAR === globalToYear && TO_WEEK > globalToWeek)
        ) {
          globalToYear = TO_YEAR;
          globalToWeek = TO_WEEK;
        }
      }
    });

    if (!anyValid || globalFromYear === 9999) {
      setErrorMessage("FROM/TO weeks not configured in selected sites!");
      setFromWeek(null);
      setToWeek(null);
      setCurrentDate(null);
      return;
    }

    setFromWeek({ YEAR: globalFromYear, WEEK: globalFromWeek });
    setToWeek({ YEAR: globalToYear, WEEK: globalToWeek });

    // Pick a default date within that range
    const now = dayjs();
    const cYear = now.isoWeekYear();
    const cWeek = now.isoWeek();
    let defaultYear = globalFromYear;
    let defaultWeek = globalFromWeek;

    if (
      isWeekInRange(
        cYear,
        cWeek,
        { YEAR: globalFromYear, WEEK: globalFromWeek },
        { YEAR: globalToYear, WEEK: globalToWeek }
      )
    ) {
      defaultYear = cYear;
      defaultWeek = cWeek;
    } else {
      const cVal = weekToNumber(cYear, cWeek);
      const fVal = weekToNumber(globalFromYear, globalFromWeek);
      const tVal = weekToNumber(globalToYear, globalToWeek);
      if (cVal > tVal) {
        setInfoMessage("Harvest is already completed for this season.");
        defaultYear = globalToYear;
        defaultWeek = globalToWeek;
      } else if (cVal < fVal) {
        setErrorMessage(
          `Planting not started yet. It starts from Week ${globalFromWeek} of Year ${globalFromYear}`
        );
      }
    }

    const defaultDate = dayjs()
      .year(defaultYear)
      .isoWeek(defaultWeek)
      .startOf("isoWeek");
    setCurrentDate(defaultDate.format("YYYY-MM-DD"));
    setErrorMessage(null);
  }, [currentSites, currentCrop]);

  // -------------------------------
  // Effects: Build 7-day date headers from currentDate
  // -------------------------------
  useEffect(() => {
    if (!currentDate) {
      setDateHeaders([]);
      return;
    }
    const start = dayjs(currentDate);
    const headers = [];
    for (let i = 0; i < 7; i++) {
      headers.push(start.add(i, "day").format("M/D/YYYY"));
    }
    setDateHeaders(headers);
    setForcastDate(dayjs(currentDate).format("YYYY-MM-DD"));
  }, [currentDate]);

  // -------------------------------
  // SINGLE-FETCH approach using fetchVarietyData (TABLE)
  // -------------------------------
  useEffect(() => {
    if (
      !currentGreenhouse?.length ||
      !currentVariety?.length ||
      !dateHeaders.length ||
      !currentSites?.length
    )
      return;

    // gather IDs
    const greenhouseIds = currentGreenhouse.map((gh) => gh.id);
    const varietyIds = currentVariety.map((v) => v.id);
    const harvestDates = dateHeaders.map((d) => dayjs(d).format("YYYY-MM-DD"));

    // Mark combos as loading
    const newVarLoading = {};
    currentSites.forEach((siteObj) => {
      const siteName = siteObj.displayName;
      currentGreenhouse.forEach((gh) => {
        currentVariety.forEach((v) => {
          const key = `${siteName}-${gh.name}-${v.name}`;
          newVarLoading[key] = true;
        });
      });
    });
    setVarietyLoadingMap((prev) => ({ ...prev, ...newVarLoading }));

    const partialDataCopy = {};

    async function fetchOneSite(siteObj) {
      const resp = await fetchVarietyData(
        currentOrg,
        siteObj.id,
        currentCrop?.id,
        greenhouseIds,
        varietyIds,
        harvestDates
      );
      if (!resp?.data?.success) {
        console.error("fetchVarietyData error:", resp?.data?.message);
        return {};
      }
      return resp.data.data || {};
    }

    async function doAllSites() {
      const siteFetchResults = await Promise.all(
        currentSites.map((s) => fetchOneSite(s))
      );

      // Combine
      const combinedData = {};
      siteFetchResults.forEach((dataObj) => {
        Object.keys(dataObj).forEach((siteKey) => {
          if (!combinedData[siteKey]) combinedData[siteKey] = {};
          const ghDict = dataObj[siteKey] || {};
          Object.keys(ghDict).forEach((ghName) => {
            if (!combinedData[siteKey][ghName]) {
              combinedData[siteKey][ghName] = {};
            }
            const varDict = ghDict[ghName] || {};
            Object.keys(varDict).forEach((vName) => {
              if (!combinedData[siteKey][ghName][vName]) {
                combinedData[siteKey][ghName][vName] = {};
              }
              const dateDict = varDict[vName] || {};
              Object.keys(dateDict).forEach((dateStr) => {
                combinedData[siteKey][ghName][vName][dateStr] =
                  dateDict[dateStr];
              });
            });
          });
        });
      });

      // parse combined
      currentSites.forEach((siteObj) => {
        const siteName = siteObj.displayName;
        if (!partialDataCopy[siteName]) partialDataCopy[siteName] = {};

        const {
          totalRows,
          areaPerRow,
          averageYieldKeyName,
          summaryAverageYieldKeyName,
          refinedAverageYieldKeyName,
          areaOfGH,
          averageYieldPerSqMeterKeyName,
        } = getSiteForecastConfig(siteObj, currentCrop?.name);

        if (!combinedData[siteName]) combinedData[siteName] = {};

        Object.keys(combinedData[siteName]).forEach((ghName) => {
          if (!partialDataCopy[siteName][ghName]) {
            partialDataCopy[siteName][ghName] = {};
          }
          const varDict = combinedData[siteName][ghName] || {};
          Object.keys(varDict).forEach((vName) => {
            if (!partialDataCopy[siteName][ghName][vName]) {
              partialDataCopy[siteName][ghName][vName] = { YieldByDate: {} };
            }
            const dateDict = varDict[vName] || {};
            Object.keys(dateDict).forEach((dateStr) => {
              const forecastJSON =
                {
                  ...(dateDict[dateStr]?.harvest_schedule || {}),
                  ...(dateDict[dateStr]?.forecast || {}),
                } || null;
              let summaryAverageYield = getTotalYield(
                forecastJSON,
                summaryAverageYieldKeyName,
                totalRows,
                areaPerRow,
                areaOfGH,
                averageYieldPerSqMeterKeyName
              );
              if (summaryAverageYield === 0) {
                summaryAverageYield = getTotalYield(
                  forecastJSON,
                  refinedAverageYieldKeyName,
                  totalRows,
                  areaPerRow,
                  areaOfGH,
                  averageYieldPerSqMeterKeyName
                );
              }
              if (summaryAverageYield === 0) {
                summaryAverageYield = getTotalYield(
                  forecastJSON,
                  averageYieldKeyName,
                  totalRows,
                  areaPerRow,
                  areaOfGH,
                  averageYieldPerSqMeterKeyName
                );
              }
              partialDataCopy[siteName][ghName][vName].YieldByDate[dateStr] =
                summaryAverageYield;
            });
          });
        });
      });

      // fill 0 if missing
      currentSites.forEach((siteObj) => {
        const siteName = siteObj.displayName;
        if (!partialDataCopy[siteName]) partialDataCopy[siteName] = {};

        currentGreenhouse.forEach((gh) => {
          if (!partialDataCopy[siteName][gh.name]) {
            partialDataCopy[siteName][gh.name] = {};
          }
          currentVariety.forEach((v) => {
            if (!partialDataCopy[siteName][gh.name][v.name]) {
              partialDataCopy[siteName][gh.name][v.name] = { YieldByDate: {} };
            }
            harvestDates.forEach((iso) => {
              const existing =
                partialDataCopy[siteName][gh.name][v.name].YieldByDate[iso];
              if (existing === undefined) {
                partialDataCopy[siteName][gh.name][v.name].YieldByDate[iso] = 0;
              }
            });
          });
        });
      });

      setAnalysisTableData(partialDataCopy);

      // mark loaded
      const loadedMap = {};
      currentSites.forEach((siteObj) => {
        const siteName = siteObj.displayName;
        currentGreenhouse.forEach((gh) => {
          currentVariety.forEach((v) => {
            const key = `${siteName}-${gh.name}-${v.name}`;
            loadedMap[key] = false;
          });
        });
      });
      setVarietyLoadingMap((prev) => ({ ...prev, ...loadedMap }));
    }

    doAllSites().catch((err) => {
      console.error("fetchVarietyData error:", err);
    });
  }, [
    dateHeaders,
    currentSites,
    currentGreenhouse,
    currentVariety,
    currentCrop,
    currentOrg,
  ]);

  // -------------------------------
  // Build displayAnalysisData
  // -------------------------------
  useEffect(() => {
    if (!dateHeaders.length) {
      setDisplayAnalysisData({});
      return;
    }

    const newDisplay = {};
    currentSites.forEach((siteObj) => {
      const siteName = siteObj.displayName;
      if (!analysisTableData[siteName]) return;
      newDisplay[siteName] = {};

      currentGreenhouse.forEach((ghObj) => {
        const ghName = ghObj.name;
        if (!analysisTableData[siteName][ghName]) return;
        newDisplay[siteName][ghName] = {};

        currentVariety.forEach((pObj) => {
          const pName = pObj.name;
          if (!analysisTableData[siteName][ghName][pName]) return;
          const { YieldByDate } = analysisTableData[siteName][ghName][pName];
          const yieldArr = dateHeaders.map((dh) => {
            const iso = dayjs(dh).format("YYYY-MM-DD");
            return YieldByDate?.[iso] ?? 0;
          });
          newDisplay[siteName][ghName][pName] = { Yield: yieldArr };
        });
      });
    });

    setDisplayAnalysisData(newDisplay);
  }, [
    analysisTableData,
    dateHeaders,
    currentSites,
    currentGreenhouse,
    currentVariety,
  ]);

  // Merge all sites if needed
  function mergeAllSitesData(originalData) {
    const all_sites_key =
      "All Sites (" + Object.keys(originalData).join(",") + ")";
    const merged = {};
    merged[all_sites_key] = {};

    Object.keys(originalData).forEach((siteName) => {
      const ghMap = originalData[siteName];
      Object.keys(ghMap).forEach((ghName) => {
        if (!merged[all_sites_key][ghName]) {
          merged[all_sites_key][ghName] = {};
        }
        const varMap = ghMap[ghName];
        Object.keys(varMap).forEach((varName) => {
          if (!merged[all_sites_key][ghName][varName]) {
            merged[all_sites_key][ghName][varName] = {};
          }
          const metricsObj = varMap[varName];
          Object.keys(metricsObj).forEach((metricKey) => {
            const arr = metricsObj[metricKey];
            if (!merged[all_sites_key][ghName][varName][metricKey]) {
              merged[all_sites_key][ghName][varName][metricKey] = Array(
                arr.length
              ).fill(0);
            }
            for (let i = 0; i < arr.length; i++) {
              merged[all_sites_key][ghName][varName][metricKey][i] += arr[i];
            }
          });
        });
      });
    });

    return merged;
  }

  function buildTotalSummary(displayData) {
    let totalCount = 0;
    let loadedCount = 0;
    for (let sKey in displayData) {
      for (let ghKey in displayData[sKey]) {
        for (let pKey in displayData[sKey][ghKey]) {
          totalCount++;
          if (!varietyLoadingMap[`${sKey}-${ghKey}-${pKey}`]) loadedCount++;
        }
      }
    }
    if (totalCount === 0) return [];
    if (loadedCount === 0) {
      return [
        {
          key: "total-summary-row-loading",
          isLoading: true,
          subsite: "Summary (All)",
          detail: "",
        },
      ];
    }

    const allMetrics = new Set();
    const sums = {};
    for (let sKey in displayData) {
      for (let ghKey in displayData[sKey]) {
        for (let pKey in displayData[sKey][ghKey]) {
          if (varietyLoadingMap[`${sKey}-${ghKey}-${pKey}`]) continue;
          const VarietyObj = displayData[sKey][ghKey][pKey];
          Object.keys(VarietyObj).forEach((mKey) => allMetrics.add(mKey));
        }
      }
    }
    const metricKeys = [...allMetrics];
    metricKeys.forEach((mk) => (sums[mk] = Array(dateHeaders.length).fill(0)));

    for (let sKey in displayData) {
      for (let ghKey in displayData[sKey]) {
        for (let pKey in displayData[sKey][ghKey]) {
          if (varietyLoadingMap[`${sKey}-${ghKey}-${pKey}`]) continue;
          const VarietyObj = displayData[sKey][ghKey][pKey];
          metricKeys.forEach((mk) => {
            const arr = VarietyObj[mk] || [];
            for (let i = 0; i < arr.length; i++) {
              sums[mk][i] += arr[i];
            }
          });
        }
      }
    }

    const row = {
      key: "total-summary-row",
      subsite: "Summary (All)",
      detail: metricKeys.join("<br>"),
    };
    dateHeaders.forEach((_, idx) => {
      const vals = metricKeys.map((mk) => sums[mk][idx] || 0);
      row[`day${idx}`] = vals.join("<br>");
    });
    return [row];
  }

  function buildSiteLevelSummary(displayData) {
    const rows = [];
    Object.keys(displayData).forEach((siteName) => {
      let totalCount = 0;
      let loadedCount = 0;
      Object.keys(displayData[siteName]).forEach((gh) => {
        Object.keys(displayData[siteName][gh]).forEach((variety) => {
          totalCount++;
          if (!varietyLoadingMap[`${siteName}-${gh}-${variety}`]) loadedCount++;
        });
      });
      if (totalCount === 0) return;
      if (loadedCount === 0) {
        rows.push({
          key: `site-summary-${siteName}-loading`,
          isLoading: true,
          subsite: siteName,
          detail: "",
        });
        return;
      }

      const allMetrics = new Set();
      Object.keys(displayData[siteName]).forEach((gh) => {
        Object.keys(displayData[siteName][gh]).forEach((variety) => {
          if (varietyLoadingMap[`${siteName}-${gh}-${variety}`]) return;
          Object.keys(displayData[siteName][gh][variety]).forEach((mKey) =>
            allMetrics.add(mKey)
          );
        });
      });
      const metricKeys = [...allMetrics];
      const sums = {};
      metricKeys.forEach(
        (mk) => (sums[mk] = Array(dateHeaders.length).fill(0))
      );

      Object.keys(displayData[siteName]).forEach((gh) => {
        Object.keys(displayData[siteName][gh]).forEach((variety) => {
          if (varietyLoadingMap[`${siteName}-${gh}-${variety}`]) return;
          const VarietyObj = displayData[siteName][gh][variety];
          metricKeys.forEach((mk) => {
            const arr = VarietyObj[mk] || [];
            for (let i = 0; i < arr.length; i++) {
              sums[mk][i] += arr[i];
            }
          });
        });
      });

      const row = {
        key: `site-summary-${siteName}`,
        subsite: siteName,
        detail: metricKeys.join("<br>"),
      };
      dateHeaders.forEach((_, idx) => {
        const vals = metricKeys.map((mk) => sums[mk][idx] || 0);
        row[`day${idx}`] = vals.join("<br>");
      });
      rows.push(row);
    });
    return rows;
  }

  // -------------------------------------
  // Summaries that need site-level total
  // -------------------------------------
  function buildGlasshouseSummary(displayData) {
    const rows = [];

    Object.keys(displayData).forEach((siteName) => {
      // We'll gather child rows first, then compute the parent's totals
      const childRows = [];
      let anyChildLoading = false;

      Object.keys(displayData[siteName]).forEach((gh) => {
        const allVarieties = Object.keys(displayData[siteName][gh]);
        const isLoading = allVarieties.some(
          (varName) => varietyLoadingMap[`${siteName}-${gh}-${varName}`]
        );
        if (isLoading) {
          anyChildLoading = true;
          childRows.push({
            key: `gh-child-loading-${siteName}-${gh}`,
            isLoading: true,
            subsite: gh,
            detail: "",
          });
          return;
        }

        // sum across all varieties in this greenhouse
        const allMetrics = new Set();
        allVarieties.forEach((varName) => {
          Object.keys(displayData[siteName][gh][varName]).forEach((m) =>
            allMetrics.add(m)
          );
        });
        const metricKeys = [...allMetrics];
        const sums = {};
        metricKeys.forEach(
          (mk) => (sums[mk] = Array(dateHeaders.length).fill(0))
        );

        allVarieties.forEach((varName) => {
          const varObj = displayData[siteName][gh][varName];
          metricKeys.forEach((mk) => {
            const arr = varObj[mk] || [];
            for (let i = 0; i < arr.length; i++) {
              sums[mk][i] += arr[i];
            }
          });
        });

        const childRow = {
          key: `gh-child-${siteName}-${gh}`,
          subsite: gh,
          detail: metricKeys.join("<br>"),
        };
        dateHeaders.forEach((_, idx) => {
          const vals = metricKeys.map((mk) => sums[mk][idx] || 0);
          childRow[`day${idx}`] = vals.join("<br>");
        });
        childRows.push(childRow);
      });

      // Now build the parent row => sum across all GH rows
      let parentRow = {
        key: `gh-parent-${siteName}`,
        isParent: true,
        subsite: siteName,
        detail: "",
        children: childRows,
      };

      if (childRows.length === 0) {
        // no child => skip
        return rows.push(parentRow);
      }

      if (anyChildLoading) {
        // If ANY greenhouse is loading => the site row is also loading
        parentRow.isLoading = true;
      } else {
        // sum across childRows
        // each child has day0..dayN columns with <br>-joined metrics
        // we want to sum them across the metrics
        // first, parse child detail => list of metrics
        const metricSet = new Set();
        childRows.forEach((c) => {
          if (!c.isLoading && c.detail) {
            c.detail.split("<br>").forEach((m) => metricSet.add(m));
          }
        });
        const metricKeys = [...metricSet];

        // build arrays
        const parentSums = {};
        metricKeys.forEach((mk) => {
          parentSums[mk] = Array(dateHeaders.length).fill(0);
        });

        // For each child row, parse each dayX
        childRows.forEach((c) => {
          if (c.isLoading) return; // skip
          if (!c.detail) return; // skip if no detail
          const rowMetrics = c.detail.split("<br>");
          // day0..dayN
          for (let d = 0; d < dateHeaders.length; d++) {
            // c[`day${d}`] might be something like "12<br>14" if multiple metrics
            // or a single number if only 1 metric
            const dayValStr = c[`day${d}`] || "0";
            const splitted = dayValStr
              .split("<br>")
              .map((x) => parseFloat(x) || 0);
            // splitted array in the same order as rowMetrics
            rowMetrics.forEach((mk, idx) => {
              parentSums[mk][d] += splitted[idx] || 0;
            });
          }
        });

        // finalize parent's "detail"
        parentRow.detail = metricKeys.join("<br>");
        // fill parent's day columns
        dateHeaders.forEach((_, dIdx) => {
          const dayVals = metricKeys.map((mk) => parentSums[mk][dIdx]);
          parentRow[`day${dIdx}`] = dayVals.join("<br>");
        });
      }

      rows.push(parentRow);
    });

    return rows;
  }

  function buildVarietyLevelSummary(displayData) {
    const rows = [];

    Object.keys(displayData).forEach((siteName) => {
      const children = [];
      let anyChildLoading = false;

      // set of all varieties
      const VarietySet = new Set();
      Object.keys(displayData[siteName]).forEach((gh) => {
        Object.keys(displayData[siteName][gh]).forEach((VarietyName) => {
          VarietySet.add(VarietyName);
        });
      });

      [...VarietySet].forEach((VarietyName) => {
        // check if loading
        const anyLoading = Object.keys(displayData[siteName]).some(
          (gh) =>
            displayData[siteName][gh][VarietyName] &&
            varietyLoadingMap[`${siteName}-${gh}-${VarietyName}`]
        );
        if (anyLoading) {
          anyChildLoading = true;
          children.push({
            key: `var-child-loading-${siteName}-${VarietyName}`,
            isLoading: true,
            subsite: VarietyName,
            detail: "",
          });
          return;
        }

        // sum across all GH for that variety
        const allMetrics = new Set();
        Object.keys(displayData[siteName]).forEach((gh) => {
          if (displayData[siteName][gh][VarietyName]) {
            Object.keys(displayData[siteName][gh][VarietyName]).forEach((m) =>
              allMetrics.add(m)
            );
          }
        });

        const metricKeys = [...allMetrics];
        const sums = {};
        metricKeys.forEach(
          (mk) => (sums[mk] = Array(dateHeaders.length).fill(0))
        );

        Object.keys(displayData[siteName]).forEach((gh) => {
          if (displayData[siteName][gh][VarietyName]) {
            const varObj = displayData[siteName][gh][VarietyName];
            metricKeys.forEach((mk) => {
              const arr = varObj[mk] || [];
              for (let i = 0; i < arr.length; i++) {
                sums[mk][i] += arr[i];
              }
            });
          }
        });

        const row = {
          key: `var-child-${siteName}-${VarietyName}`,
          subsite: VarietyName,
          detail: metricKeys.join("<br>"),
        };
        dateHeaders.forEach((_, idx) => {
          const vals = metricKeys.map((mk) => sums[mk][idx] || 0);
          row[`day${idx}`] = vals.join("<br>");
        });
        children.push(row);
      });

      // now parent row with total across children
      const siteParent = {
        key: `var-parent-${siteName}`,
        isParent: true,
        subsite: siteName,
        detail: "",
        children,
      };

      if (!children.length) {
        rows.push(siteParent);
        return;
      }

      if (anyChildLoading) {
        siteParent.isLoading = true;
      } else {
        // parse each child row => sum into siteParent
        // gather all metrics from child "detail"
        const allChildMetrics = new Set();
        children.forEach((c) => {
          if (!c.isLoading && c.detail) {
            c.detail.split("<br>").forEach((m) => allChildMetrics.add(m));
          }
        });
        const metricKeys = [...allChildMetrics];
        const sums = {};
        metricKeys.forEach(
          (mk) => (sums[mk] = Array(dateHeaders.length).fill(0))
        );

        // accumulate
        children.forEach((c) => {
          if (c.isLoading || !c.detail) return;
          const rowMetrics = c.detail.split("<br>");
          for (let d = 0; d < dateHeaders.length; d++) {
            const dayValStr = c[`day${d}`] || "0";
            const splitted = dayValStr
              .split("<br>")
              .map((x) => parseFloat(x) || 0);
            rowMetrics.forEach((mk, idx) => {
              sums[mk][d] += splitted[idx] || 0;
            });
          }
        });

        // fill parent's detail, day0..dayN
        siteParent.detail = metricKeys.join("<br>");
        dateHeaders.forEach((_, dIdx) => {
          const dayVals = metricKeys.map((mk) => sums[mk][dIdx]);
          siteParent[`day${dIdx}`] = dayVals.join("<br>");
        });
      }

      rows.push(siteParent);
    });
    return rows;
  }

  function buildDetailedAnalysis(displayData) {
    const rows = [];

    Object.keys(displayData).forEach((siteName) => {
      // We'll accumulate children in "siteParent.children"
      // Then sum up for site-level row
      const siteParent = {
        key: `detail-site-${siteName}`,
        isParent: true,
        subsite: siteName,
        detail: "",
        children: [],
      };

      let anyChildLoading = false;

      // Build a map of variety -> array of greenhouse
      const VarietyMap = {};
      Object.keys(displayData[siteName]).forEach((gh) => {
        Object.keys(displayData[siteName][gh]).forEach((VarietyName) => {
          if (!VarietyMap[VarietyName]) VarietyMap[VarietyName] = [];
          VarietyMap[VarietyName].push(gh);
        });
      });

      Object.keys(VarietyMap).forEach((VarietyName) => {
        const ghList = VarietyMap[VarietyName];
        const anyLoading = ghList.some(
          (gh) => varietyLoadingMap[`${siteName}-${gh}-${VarietyName}`]
        );
        if (anyLoading) {
          anyChildLoading = true;
          const loadRow = {
            key: `load-detail-${siteName}-${VarietyName}`,
            isLoading: true,
            subsite: VarietyName,
            detail: "",
          };
          const VarietyParent = {
            key: `variety-parent-${siteName}-${VarietyName}`,
            isParent: true,
            subsite: VarietyName,
            detail: "",
            children: [loadRow],
          };
          siteParent.children.push(VarietyParent);
          return;
        }

        // Summation for "Summary <VarietyName>"
        const allMetrics = new Set();
        ghList.forEach((gh) => {
          Object.keys(displayData[siteName][gh][VarietyName]).forEach((m) =>
            allMetrics.add(m)
          );
        });
        const metricKeys = [...allMetrics];
        const sums = {};
        metricKeys.forEach(
          (mk) => (sums[mk] = Array(dateHeaders.length).fill(0))
        );

        ghList.forEach((gh) => {
          const VarietyObj = displayData[siteName][gh][VarietyName];
          metricKeys.forEach((mk) => {
            const arr = VarietyObj[mk] || [];
            for (let i = 0; i < arr.length; i++) {
              sums[mk][i] += arr[i];
            }
          });
        });

        const summaryRow = {
          key: `summary-${siteName}-${VarietyName}`,
          isSummary: true,
          subsite: `Summary ${VarietyName}`,
          detail: metricKeys.join("<br>"),
        };
        dateHeaders.forEach((_, idx) => {
          const vals = metricKeys.map((mk) => sums[mk][idx] || 0);
          summaryRow[`day${idx}`] = vals.join("<br>");
        });

        // Then child rows for each GH
        const ghRows = [];
        ghList.forEach((gh) => {
          const VarietyObj = displayData[siteName][gh][VarietyName];
          const rowMetrics = Object.keys(VarietyObj);
          const row = {
            key: `ghrow-${siteName}-${gh}-${VarietyName}`,
            subsite: `${siteName} - ${gh} - ${VarietyName}`,
            detail: rowMetrics.join("<br>"),
          };
          for (let d = 0; d < dateHeaders.length; d++) {
            const vals = rowMetrics.map((mk) => VarietyObj[mk][d] || 0);
            row[`day${d}`] = vals.join("<br>");
          }
          ghRows.push(row);
        });

        const VarietyParent = {
          key: `variety-parent-${siteName}-${VarietyName}`,
          isParent: true,
          subsite: VarietyName,
          detail: "",
          children: [summaryRow, ...ghRows],
        };
        siteParent.children.push(VarietyParent);
      });

      if (!siteParent.children.length) {
        rows.push(siteParent);
        return;
      }

      // Now compute parent's total across all child variety parents
      // If any variety is loading => site row is loading
      if (anyChildLoading) {
        siteParent.isLoading = true;
        rows.push(siteParent);
        return;
      }

      // Otherwise, sum across all children
      // We'll parse each child's "summary" row and each GH row
      // But easiest is to parse the final "summary row" for each variety
      // because that row already sums across GH.
      // The child variety parent has a child "summary" row with key = `summary-${siteName}-${VarietyName}`

      // 1) gather all metric keys from these summary rows
      const finalMetricSet = new Set();
      siteParent.children.forEach((varParent) => {
        // its children => first is summary row => detail => metrics
        const summaryChild = varParent.children?.[0];
        if (summaryChild && summaryChild.detail) {
          summaryChild.detail
            .split("<br>")
            .forEach((m) => finalMetricSet.add(m));
        }
      });
      const finalMetrics = [...finalMetricSet];
      const sums = {};
      finalMetrics.forEach(
        (mk) => (sums[mk] = Array(dateHeaders.length).fill(0))
      );

      // 2) accumulate
      siteParent.children.forEach((varParent) => {
        const summaryChild = varParent.children?.[0];
        if (!summaryChild || !summaryChild.detail || summaryChild.isLoading)
          return;
        const rowMetrics = summaryChild.detail.split("<br>");
        for (let d = 0; d < dateHeaders.length; d++) {
          const dayValStr = summaryChild[`day${d}`] || "0";
          const splitted = dayValStr
            .split("<br>")
            .map((x) => parseFloat(x) || 0);
          rowMetrics.forEach((mk, idx) => {
            sums[mk][d] += splitted[idx] || 0;
          });
        }
      });

      // 3) Fill siteParent's detail & day columns
      siteParent.detail = finalMetrics.join("<br>");
      dateHeaders.forEach((_, dIdx) => {
        const dayVals = finalMetrics.map((mk) => sums[mk][dIdx]);
        siteParent[`day${dIdx}`] = dayVals.join("<br>");
      });

      rows.push(siteParent);
    });

    return rows;
  }

  // -------------------------------
  // Build final dataSource & table loading
  // -------------------------------
  useEffect(() => {
    if (!Object.keys(displayAnalysisData).length || !dateHeaders.length) {
      setAnalysisDataSource([]);
      const anyLoading = Object.values(varietyLoadingMap).some(Boolean);
      setTableLoading(anyLoading);
      return;
    }

    // If merging all sites
    let finalDisplayData = displayAnalysisData;
    if (showMergeSwitch && mergeAllSites) {
      finalDisplayData = mergeAllSitesData(displayAnalysisData);
    }

    let finalData = [];
    switch (reportView) {
      case "Summary (All)":
        finalData = buildTotalSummary(finalDisplayData);
        break;
      case "Summary (Site)":
        finalData = buildSiteLevelSummary(finalDisplayData);
        break;
      case "Summary (GH)":
        finalData = buildGlasshouseSummary(finalDisplayData);
        break;
      case "Summary (Variety)":
        finalData = buildVarietyLevelSummary(finalDisplayData);
        break;
      case "Detailed":
      default:
        finalData = buildDetailedAnalysis(finalDisplayData);
        break;
    }
    setAnalysisDataSource(finalData);

    const anyLoading = Object.values(varietyLoadingMap).some(Boolean);
    const hasRows = finalData.length > 0;
    setTableLoading(!hasRows && anyLoading);

    // Expand all rows
    const allKeys = [];
    const gatherKeys = (rows) => {
      rows.forEach((r) => {
        allKeys.push(r.key);
        if (r.children) gatherKeys(r.children);
      });
    };
    gatherKeys(finalData);
    setExpandedKeys(allKeys);

    // Show/hide metric column
    const distinctMetrics = new Set();
    const gatherMetrics = (row) => {
      if (row.detail) {
        row.detail.split("<br>").forEach((m) => distinctMetrics.add(m.trim()));
      }
      if (row.children) row.children.forEach((c) => gatherMetrics(c));
    };
    finalData.forEach((r) => gatherMetrics(r));
    setShowMetricColumn(distinctMetrics.size > 1);
  }, [
    displayAnalysisData,
    dateHeaders,
    reportView,
    varietyLoadingMap,
    mergeAllSites,
    showMergeSwitch,
  ]);

  // ---------------------------------------------------------------
  // GRAPH
  // ---------------------------------------------------------------
  async function buildGraphData() {
    if (!fromWeek || !toWeek) return [];
    if (
      !currentSites?.length ||
      !currentGreenhouse?.length ||
      !currentVariety?.length
    )
      return [];

    setGraphLoading(true);

    const fromDay = dayjs()
      .year(fromWeek.YEAR)
      .isoWeek(fromWeek.WEEK)
      .startOf("isoWeek");
    const toDay = dayjs()
      .year(toWeek.YEAR)
      .isoWeek(toWeek.WEEK)
      .endOf("isoWeek");

    const allDates = [];
    let cursor = fromDay.clone();
    while (cursor.isBefore(toDay) || cursor.isSame(toDay, "day")) {
      allDates.push(cursor.format("YYYY-MM-DD"));
      cursor = cursor.add(1, "day");
    }

    const greenhouseIds = currentGreenhouse.map((g) => g.id);
    const varietyIds = currentVariety.map((v) => v.id);

    const combinedGraphData = {};

    async function fetchOneSiteGraph(siteObj) {
      const resp = await fetchVarietyData(
        currentOrg,
        siteObj.id,
        currentCrop?.id,
        greenhouseIds,
        varietyIds,
        allDates
      );
      if (!resp?.data?.success) {
        console.error("fetchVarietyData (graph) error:", resp?.data?.message);
        return {};
      }
      return resp.data.data || {};
    }

    const siteResults = await Promise.all(
      currentSites.map((s) => fetchOneSiteGraph(s))
    );

    // Combine
    siteResults.forEach((dataObj) => {
      Object.keys(dataObj).forEach((siteKey) => {
        if (!combinedGraphData[siteKey]) combinedGraphData[siteKey] = {};
        const ghDict = dataObj[siteKey] || {};
        Object.keys(ghDict).forEach((ghName) => {
          if (!combinedGraphData[siteKey][ghName]) {
            combinedGraphData[siteKey][ghName] = {};
          }
          const varDict = ghDict[ghName] || {};
          Object.keys(varDict).forEach((vName) => {
            if (!combinedGraphData[siteKey][ghName][vName]) {
              combinedGraphData[siteKey][ghName][vName] = {};
            }
            const dateDict = varDict[vName] || {};
            Object.keys(dateDict).forEach((dateStr) => {
              combinedGraphData[siteKey][ghName][vName][dateStr] =
                dateDict[dateStr];
            });
          });
        });
      });
    });

    // parse to analysis shape
    let graphAnalysisData = {};

    currentSites.forEach((siteObj) => {
      const siteName = siteObj.displayName;
      const {
        totalRows,
        areaPerRow,
        averageYieldKeyName,
        summaryAverageYieldKeyName,
        refinedAverageYieldKeyName,
        areaOfGH,
        averageYieldPerSqMeterKeyName,
      } = getSiteForecastConfig(siteObj, currentCrop?.name);

      if (!graphAnalysisData[siteName]) {
        graphAnalysisData[siteName] = {};
      }
      if (!combinedGraphData[siteName]) combinedGraphData[siteName] = {};

      currentGreenhouse.forEach((ghObj) => {
        const ghName = ghObj.name;
        if (!graphAnalysisData[siteName][ghName]) {
          graphAnalysisData[siteName][ghName] = {};
        }
        if (!combinedGraphData[siteName][ghName]) {
          combinedGraphData[siteName][ghName] = {};
        }

        currentVariety.forEach((varObj) => {
          const varName = varObj.name;
          if (!graphAnalysisData[siteName][ghName][varName]) {
            graphAnalysisData[siteName][ghName][varName] = {};
          }
          if (!combinedGraphData[siteName][ghName][varName]) {
            combinedGraphData[siteName][ghName][varName] = {};
          }

          allDates.forEach((dateStr, idx) => {
            const harvest_schedule =
              combinedGraphData[siteName][ghName][varName][dateStr]
                ?.harvest_schedule || {};
            const forecast =
              combinedGraphData[siteName][ghName][varName][dateStr]?.forecast ||
              {};
            const forecastObj = { ...harvest_schedule, ...forecast } || null;

            // Check if we have keys in metadata
            const graphKeysConfig =
              siteMetadata?.forecast?.REPORTING_GRAPH_KEYS || [];
              
            // Extract the actual key names to look for in the data
            const graphKeys = graphKeysConfig.map(config => typeof config === 'object' ? config.name : config);
            
            // Create a mapping from actual key name to display name
            const keyDisplayMap = {};
            graphKeysConfig.forEach(config => {
              if (typeof config === 'object' && config.name && config.display_name) {
                keyDisplayMap[config.name] = config.display_name;
              }
            });

            // If we have keys in metadata, use those, otherwise use all keys
            const keysToUse =
              graphKeys.length > 0
                ? graphKeys
                : [...Object.keys(harvest_schedule), ...Object.keys(forecast)];

            // Deduplicate keys
            const uniqueKeys = [...new Set(keysToUse)];

            // Process each key
            uniqueKeys.forEach((key) => {
              // Initialize array for this key if it doesn't exist
              if (!graphAnalysisData[siteName][ghName][varName][key]) {
                graphAnalysisData[siteName][ghName][varName][key] = Array(
                  allDates.length
                ).fill(0);
              }

              // If key exists directly in harvest_schedule, use that value
              if (key in harvest_schedule) {
                graphAnalysisData[siteName][ghName][varName][key][idx] =
                  harvest_schedule[key] || 0;
              }
              // Otherwise calculate it using getTotalYield
              else {
                graphAnalysisData[siteName][ghName][varName][key][idx] =
                  getTotalYield(
                    forecastObj,
                    key,
                    totalRows,
                    areaPerRow,
                    areaOfGH,
                    averageYieldPerSqMeterKeyName
                  );
              }
              
              // Store the display name mapping alongside the data
              if (!graphAnalysisData[siteName][ghName][varName]["__displayNames"]) {
                graphAnalysisData[siteName][ghName][varName]["__displayNames"] = {};
              }
              graphAnalysisData[siteName][ghName][varName]["__displayNames"][key] = 
                keyDisplayMap[key] || key;
            });
          });
        });
      });
    });

    if (showMergeSwitch && mergeAllSites) {
      const all_sites_key =
        "All Sites (" + Object.keys(graphAnalysisData).join(",") + ")";
      const merged = {};
      merged[all_sites_key] = {};
      Object.keys(graphAnalysisData).forEach((siteName) => {
        Object.keys(graphAnalysisData[siteName]).forEach((ghName) => {
          if (!merged[all_sites_key][ghName]) {
            merged[all_sites_key][ghName] = {};
          }
          const varMap = graphAnalysisData[siteName][ghName];
          Object.keys(varMap).forEach((varName) => {
            if (!merged[all_sites_key][ghName][varName]) {
              merged[all_sites_key][ghName][varName] = {};
            }
            const metricsObj = varMap[varName];
            Object.keys(metricsObj).forEach((metricKey) => {
              const arr = metricsObj[metricKey];
              if (!merged[all_sites_key][ghName][varName][metricKey]) {
                merged[all_sites_key][ghName][varName][metricKey] = Array(
                  arr.length
                ).fill(0);
              }
              for (let i = 0; i < arr.length; i++) {
                merged[all_sites_key][ghName][varName][metricKey][i] += arr[i];
              }
            });
          });
        });
      });
      graphAnalysisData[all_sites_key] = merged[all_sites_key];
    }

    // Convert graphAnalysisData => lines
    let lines = [];
    switch (reportView) {
      case "Summary (All)": {
        // Get all possible metrics across all entities
        const allMetrics = new Set();
        for (let siteKey in graphAnalysisData) {
          for (let ghKey in graphAnalysisData[siteKey]) {
            for (let varKey in graphAnalysisData[siteKey][ghKey]) {
              Object.keys(graphAnalysisData[siteKey][ghKey][varKey]).forEach(
                (metric) => {
                  if (metric !== "__displayNames") {
                    allMetrics.add(metric);
                  }
                }
              );
            }
          }
        }
        
        // For each metric, create a summary line
        Array.from(allMetrics).forEach((metric, metricIndex) => {
          const sumArr = Array(allDates.length).fill(0);
          let displayName = metric;
          
          // Find a display name for this metric
          for (let siteKey in graphAnalysisData) {
            for (let ghKey in graphAnalysisData[siteKey]) {
              for (let varKey in graphAnalysisData[siteKey][ghKey]) {
                if (graphAnalysisData[siteKey][ghKey][varKey]["__displayNames"] && 
                    graphAnalysisData[siteKey][ghKey][varKey]["__displayNames"][metric]) {
                  displayName = graphAnalysisData[siteKey][ghKey][varKey]["__displayNames"][metric];
                  break;
                }
              }
              if (displayName !== metric) break;
            }
            if (displayName !== metric) break;
          }
          
          for (let siteKey in graphAnalysisData) {
            for (let ghKey in graphAnalysisData[siteKey]) {
              for (let varKey in graphAnalysisData[siteKey][ghKey]) {
                const metricArr =
                  graphAnalysisData[siteKey][ghKey][varKey][metric] || [];
                for (let i = 0; i < metricArr.length; i++) {
                  sumArr[i] += metricArr[i];
                }
              }
            }
          }
          const dataPoints = allDates.map((d, i) => ({
            x: dayjs(d).format(UI_DATE_FORMAT),
            y: sumArr[i],
          }));
          lines.push({ 
            name: `${displayName}`, 
            data: dataPoints, 
            color: COLORS.graphColors[metricIndex % COLORS.graphColors.length],
          });
        });
        break;
      }
      case "Summary (Site)": {
        // For each site, show all metrics
        let metricIndex = 0;
        for (let siteKey in graphAnalysisData) {
          // Get all metrics for this site
          const siteMetrics = new Set();
          for (let ghKey in graphAnalysisData[siteKey]) {
            for (let varKey in graphAnalysisData[siteKey][ghKey]) {
              Object.keys(graphAnalysisData[siteKey][ghKey][varKey]).forEach(
                (metric) => {
                  if (metric !== "__displayNames") {
                    siteMetrics.add(metric);
                  }
                }
              );
            }
          }
          
          // For each metric, create a site summary line
          Array.from(siteMetrics).forEach((metric) => {
            const sumArr = Array(allDates.length).fill(0);
            let displayName = metric;
            
            // Find a display name for this metric
            for (let ghKey in graphAnalysisData[siteKey]) {
              for (let varKey in graphAnalysisData[siteKey][ghKey]) {
                if (graphAnalysisData[siteKey][ghKey][varKey]["__displayNames"] && 
                    graphAnalysisData[siteKey][ghKey][varKey]["__displayNames"][metric]) {
                  displayName = graphAnalysisData[siteKey][ghKey][varKey]["__displayNames"][metric];
                  break;
                }
              }
              if (displayName !== metric) break;
            }
            
            for (let ghKey in graphAnalysisData[siteKey]) {
              for (let varKey in graphAnalysisData[siteKey][ghKey]) {
                const metricArr =
                  graphAnalysisData[siteKey][ghKey][varKey][metric] || [];
                for (let i = 0; i < metricArr.length; i++) {
                  sumArr[i] += metricArr[i];
                }
              }
            }
            const dataPoints = allDates.map((d, i) => ({
              x: dayjs(d).format(UI_DATE_FORMAT),
              y: sumArr[i],
            }));
            lines.push({ 
              name: `${siteKey} - ${displayName}`, 
              data: dataPoints,
              color:
                COLORS.graphColors[metricIndex % COLORS.graphColors.length],
            });
            metricIndex++;
          });
        }
        break;
      }
      case "Summary (GH)": {
        let metricIndex = 0;
        for (let siteKey in graphAnalysisData) {
          for (let ghKey in graphAnalysisData[siteKey]) {
            // Get all metrics for this greenhouse
            const ghMetrics = new Set();
            for (let varKey in graphAnalysisData[siteKey][ghKey]) {
              Object.keys(graphAnalysisData[siteKey][ghKey][varKey]).forEach(
                (metric) => {
                  if (metric !== "__displayNames") {
                    ghMetrics.add(metric);
                  }
                }
              );
            }
            
            // For each metric, create a greenhouse summary line
            Array.from(ghMetrics).forEach((metric) => {
              const sumArr = Array(allDates.length).fill(0);
              let displayName = metric;
              
              // Find a display name for this metric
              for (let varKey in graphAnalysisData[siteKey][ghKey]) {
                if (graphAnalysisData[siteKey][ghKey][varKey]["__displayNames"] && 
                    graphAnalysisData[siteKey][ghKey][varKey]["__displayNames"][metric]) {
                  displayName = graphAnalysisData[siteKey][ghKey][varKey]["__displayNames"][metric];
                  break;
                }
              }
              
              for (let varKey in graphAnalysisData[siteKey][ghKey]) {
                const metricArr =
                  graphAnalysisData[siteKey][ghKey][varKey][metric] || [];
                for (let i = 0; i < metricArr.length; i++) {
                  sumArr[i] += metricArr[i];
                }
              }
              const dataPoints = allDates.map((d, i) => ({
                x: dayjs(d).format(UI_DATE_FORMAT),
                y: sumArr[i],
              }));
              lines.push({ 
                name: `${siteKey} - ${ghKey} - ${displayName}`, 
                data: dataPoints,
                color:
                  COLORS.graphColors[metricIndex % COLORS.graphColors.length],
              });
              metricIndex++;
            });
          }
        }
        break;
      }
      case "Summary (Variety)": {
        let metricIndex = 0;
        for (let siteKey in graphAnalysisData) {
          // For each variety, get all metrics across greenhouses
          const varietyMetrics = {};
          
          for (let ghKey in graphAnalysisData[siteKey]) {
            for (let varKey in graphAnalysisData[siteKey][ghKey]) {
              if (!varietyMetrics[varKey]) {
                varietyMetrics[varKey] = new Set();
              }
              Object.keys(graphAnalysisData[siteKey][ghKey][varKey]).forEach(
                (metric) => {
                  if (metric !== "__displayNames") {
                    varietyMetrics[varKey].add(metric);
                  }
                }
              );
            }
          }
          
          // For each variety and metric, create a line
          for (let varKey in varietyMetrics) {
            Array.from(varietyMetrics[varKey]).forEach((metric) => {
              const sumArr = Array(allDates.length).fill(0);
              let displayName = metric;
              
              // Find a display name for this metric
              for (let ghKey in graphAnalysisData[siteKey]) {
                if (graphAnalysisData[siteKey][ghKey][varKey] && 
                    graphAnalysisData[siteKey][ghKey][varKey]["__displayNames"] && 
                    graphAnalysisData[siteKey][ghKey][varKey]["__displayNames"][metric]) {
                  displayName = graphAnalysisData[siteKey][ghKey][varKey]["__displayNames"][metric];
                  break;
                }
              }
              
              for (let ghKey in graphAnalysisData[siteKey]) {
                if (graphAnalysisData[siteKey][ghKey][varKey]) {
                  const metricArr =
                    graphAnalysisData[siteKey][ghKey][varKey][metric] || [];
                  for (let i = 0; i < metricArr.length; i++) {
                    sumArr[i] += metricArr[i];
                  }
                }
              }
              const dataPoints = allDates.map((d, i) => ({
                x: dayjs(d).format(UI_DATE_FORMAT),
                y: sumArr[i],
              }));
              lines.push({ 
                name: `${siteKey} - ${varKey} - ${displayName}`, 
                data: dataPoints,
                color:
                  COLORS.graphColors[metricIndex % COLORS.graphColors.length],
              });
              metricIndex++;
            });
          }
        }
        break;
      }
      case "Detailed":
      default: {
        let metricIndex = 0;
        for (let siteKey in graphAnalysisData) {
          for (let ghKey in graphAnalysisData[siteKey]) {
            for (let varKey in graphAnalysisData[siteKey][ghKey]) {
              // Get all metrics for this variety in this greenhouse
              const metrics = Object.keys(
                graphAnalysisData[siteKey][ghKey][varKey]
              ).filter(key => key !== "__displayNames");
              
              metrics.forEach((metric) => {
                const metricArr =
                  graphAnalysisData[siteKey][ghKey][varKey][metric] || [];
                
                // Get the display name if available
                const displayName = 
                  (graphAnalysisData[siteKey][ghKey][varKey]["__displayNames"] && 
                   graphAnalysisData[siteKey][ghKey][varKey]["__displayNames"][metric]) || metric;
                
                const dataPoints = allDates.map((d, i) => ({
                  x: dayjs(d).format(UI_DATE_FORMAT),
                  y: metricArr[i] || 0,
                }));
                lines.push({ 
                  name: `${siteKey} - ${ghKey} - ${varKey} - ${displayName}`, 
                  data: dataPoints,
                  color:
                    COLORS.graphColors[metricIndex % COLORS.graphColors.length],
                });
                metricIndex++;
              });
            }
          }
        }
        break;
      }
    }

    // filter out empty columns
    const filteredLines = filterEmptyDates(lines);
    setGraphDateHeaders(buildFilteredDateHeaders(filteredLines));
    setGraphLoading(false);
    return filteredLines;
  }

  const handleGenerateGraph = async () => {
    setGraphVisible(true);
    const lines = await buildGraphData();
    setChartLines(lines || []);
    setGraphLoading(false);
  };

  const disabledDate = (current) => {
    if (!fromWeek || !toWeek || !current) return true;
    const cy = current.isoWeekYear();
    const cw = current.isoWeek();
    return !isWeekInRange(cy, cw, fromWeek, toWeek);
  };

  return (
    <div id="dashboard">
      <Sidebar
        active="reporting"
        sidebarType="TomatoGrower"
        open={sidebarOpen}
        setOpen={setSidebarOpen}
      />
      <div
        className={`flex flex-col w-full px-20 ${
          sidebarOpen ? "pl-60" : ""
        } pt-10 md:pt-4 pb-14 md:pl-15 md:pr-2 sm:pr-10 justify-center transition-padding ease-in-out duration-200`}>
        <Dropdowns
          siteMetadata={siteMetadata}
          setSiteMetadata={setSiteMetadata}
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          setOrgData={setOrgData}
          orgLoading={orgLoading}
          setOrgLoading={setOrgLoading}
          currentSites={currentSites}
          setCurrentSites={setCurrentSites}
          siteData={siteData}
          setSiteData={setSiteData}
          siteLoading={siteLoading}
          setSiteLoading={setSiteLoading}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
          setCropData={setCropData}
          cropLoading={cropLoading}
          setCropLoading={setCropLoading}
          greenhouseData={greenhouseData}
          setGreenhouseData={setGreenhouseData}
          currentGreenhouse={currentGreenhouse}
          setCurrentGreenhouse={setCurrentGreenhouse}
          varietyData={varietyData}
          setVarietyData={setVarietyData}
          currentVariety={currentVariety}
          setCurrentVariety={setCurrentVariety}
          currentDate={currentDate}
          setCurrentDate={setCurrentDate}
          disabledDate={disabledDate}
          currentMetric={currentMetric}
          setCurrentMetric={setCurrentMetric}
          metricType={metricType}
          setMetricType={setMetricType}
          metricData={metricData}
          setMetricData={setMetricData}
          setDropdownLoading={setDropdownLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          settingDefaults={settingDefaults}
          setSettingDefaults={setSettingDefaults}
        />

        {errorMessage && !dropdownLoading && (
          <div className="text-2xl text-center text-dangerRed">
            {errorMessage}
          </div>
        )}
        {infoMessage && !dropdownLoading && (
          <div className="text-2xl text-center text-primaryBlue">
            {infoMessage}
          </div>
        )}

        <div className="w-full flex flex-wrap gap-4 justify-between sm:flex-col mb-10 dropdowns !font-MuseoSans">
          <TopDropdown
            value={reportView}
            classString="flex-1 min-w-[200px] sm:mb-2"
            placeholder="Select View type"
            changeCallback={setReportView}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            notFound={orgLoading ? <LoadingIcon /> : null}
            data={viewType.map((type) => ({
              value: type.value,
              label: type.label,
            }))}
          />

          {showMergeSwitch && (
            <div className="flex-1 min-w-[200px] sm:mb-2 self-center text-right">
              <span className="mr-4">Merge All Sites</span>
              <Switch
                checked={mergeAllSites}
                onChange={(checked) => setMergeAllSites(checked)}
              />
            </div>
          )}

          <Button
            type="primary"
            className="rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 transform hover:scale-105"
            icon={<LineChartOutlined />}
            size="middle"
            disabled={
              tableLoading ||
              dropdownLoading ||
              graphVisible ||
              graphLoading ||
              !currentGreenhouse?.length ||
              !currentVariety?.length
            }
            onClick={handleGenerateGraph}>
            Generate Graph
          </Button>
        </div>

        <div
          className="bg-white rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 transform hover:scale-101"
          style={{ overflowX: "auto" }}>
          <AnalysisTable
            dateHeaders={dateHeaders}
            showMetricColumn={showMetricColumn}
            analysisDataSource={analysisDataSource}
            tableLoading={tableLoading}
            expandedKeys={expandedKeys}
            setExpandedKeys={setExpandedKeys}
          />
        </div>
      </div>
      <Footer sidebarOpen={sidebarOpen} />
      <Graph
        visible={graphVisible}
        onClose={() => setGraphVisible(false)}
        graphDateHeaders={graphDateHeaders}
        chartLines={chartLines}
        viewType={reportView}
        graphLoading={graphLoading}
      />
    </div>
  );
};

export default Reporting;
